// src/features/courses/components/SearchResults.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { titleCase } from '../utils/format';

/**
 * Search results dropdown component.
 * Displays streamed search results in a floating panel.
 *
 * @param {object} props
 * @param {Array} props.results - Array of search result objects
 * @param {boolean} props.isSearching - Whether search is in progress
 * @param {string|null} props.error - Error message if any
 * @param {string} props.query - Current search query
 * @param {function} props.onClose - Callback to close the dropdown
 * @param {number|null} props.totalResults - Total results count from server
 */
export default function SearchResults({
  results,
  isSearching,
  error,
  query,
  onClose,
  totalResults,
}) {
  const navigate = useNavigate();

  const handleResultClick = (course) => {
    try {
      sessionStorage.setItem('ui.currentCourseTitle', course.title || '');
    } catch {}
    navigate(`/courses/${course.courseId}`);
    onClose?.();
  };

  const handleKeyDown = (e, course) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleResultClick(course);
    }
    if (e.key === 'Escape') {
      onClose?.();
    }
  };

  // Don't render if no query
  if (!query?.trim()) return null;

  return (
    <div
      className="search-results-dropdown"
      role="listbox"
      aria-label="Search results"
    >
      {/* Loading state */}
      {isSearching && results.length === 0 && (
        <div className="search-results-loading">
          <div className="search-spinner" aria-hidden="true" />
          <span>Searching...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="search-results-error" role="alert">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4m0 4h.01" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Results list */}
      {results.length > 0 && (
        <>
          <ul className="search-results-list">
            {results.map((course, index) => (
              <li
                key={course.courseId}
                role="option"
                aria-selected="false"
                tabIndex={0}
                className="search-result-item"
                onClick={() => handleResultClick(course)}
                onKeyDown={(e) => handleKeyDown(e, course)}
                style={{ animationDelay: `${index * 30}ms` }}
              >
                <div className="search-result-content">
                  <div className="search-result-header">
                    <span className="search-result-title">{course.title}</span>
                    {course.bookmarked && (
                      <svg
                        className="search-result-bookmark"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                      </svg>
                    )}
                  </div>
                  {course.description && (
                    <p className="search-result-description">{course.description}</p>
                  )}
                  <div className="search-result-meta">
                    {course.tag && (
                      <span className="search-result-chip">{titleCase(course.tag)}</span>
                    )}
                    {course.difficulty && (
                      <span className="search-result-chip search-result-chip-neutral">
                        {titleCase(course.difficulty)}
                      </span>
                    )}
                  </div>
                </div>
                <svg
                  className="search-result-arrow"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </li>
            ))}
          </ul>

          {/* Results count footer */}
          {!isSearching && totalResults !== null && (
            <div className="search-results-footer">
              <span className="hint">
                {totalResults === 1 ? '1 result' : `${totalResults} results`}
              </span>
            </div>
          )}

          {/* Still loading indicator when results are streaming */}
          {isSearching && results.length > 0 && (
            <div className="search-results-streaming">
              <div className="search-spinner-small" aria-hidden="true" />
            </div>
          )}
        </>
      )}

      {/* No results state */}
      {!isSearching && !error && query.trim() && results.length === 0 && (
        <div className="search-results-empty">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.2-3.2" />
          </svg>
          <span>No courses found for "{query}"</span>
        </div>
      )}
    </div>
  );
}
